import { Link } from "wouter";

function Terms() {
  return (
    <main id="top" className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
      <Link href="/login" className="text-sm text-primary underline underline-offset-4">
        Return to sign in
      </Link>

      <header className="mt-8">
        <p className="text-sm font-medium text-muted-foreground">180Pi Climate Risk</p>
        <h1 className="mt-2 text-3xl font-bold">Terms of Service &amp; Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">Draft prepared: 7 September 2026</p>
        <p className="mt-5 leading-7">
          Please read these terms and the privacy information below before using 180Pi Climate
          Risk. They explain your responsibilities, the limitations of climate risk assessments,
          and how information is handled when you use the service.
        </p>
      </header>

      {/* Keep this notice until the operator confirms the outstanding legal and operational facts. */}
      <aside aria-label="Draft status" className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
        <p className="font-semibold">Draft for review — not yet the final published policy</p>
        <p className="mt-2">
          The server location, international-transfer arrangements, and deletion and backup
          retention details still require confirmation. The note in Privacy Policy section 6
          identifies the information to complete before this policy takes effect.
        </p>
      </aside>

      <nav aria-label="Legal page sections" className="my-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-primary underline underline-offset-4">
        <a href="#terms-of-service">Terms of Service</a>
        <a href="#privacy-policy">Privacy Policy</a>
        <a href="#contact">Contact</a>
      </nav>

      <div className="space-y-12 leading-7 [&_h2]:text-2xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-7 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
        <section id="terms-of-service" aria-labelledby="terms-heading" className="scroll-mt-6 space-y-6">
          <h2 id="terms-heading">Terms of Service</h2>

          <div>
            <h3>1. About the service and these terms</h3>
            <p>
              180Pi Climate Risk helps energy and agriculture businesses assess physical climate
              risks affecting their assets and value chains. It is intended for businesses in
              East and Southern Africa, including Kenya, Uganda, Malawi, Tanzania, and Rwanda.
              Geographic data coverage varies and must be checked for each assessment; availability
              in a country does not guarantee climate data for every site. It combines site and
              business information
              with climate hazard data to support risk screening and adaptation planning.
              References to “180Pi”, “we”, “us”, and “our” mean the operator identified in the
              contact section: OneEightyPi, Ahtri 12, Tallinn 15551, Estonia. “You” means the
              individual using the service and, where applicable,
              the organisation they are authorised to represent.
            </p>
            <p>
              These terms apply when you accept them as part of registration or an agreed service
              arrangement. You must have authority to submit your organisation’s information and
              use the service on its behalf. The service is intended for professional business use.
            </p>
            <p>
              Access is currently offered as a free pilot. The pilot may evolve as features,
              datasets, and methods are evaluated. Taking part does not oblige you to purchase a
              future paid service. Any future charges will be communicated separately and require
              your agreement before they apply. You may stop using the pilot at any time and
              contact us to request account closure.
            </p>
          </div>

          <div>
            <h3>2. Accounts and acceptable use</h3>
            <p>
              Provide accurate registration information, keep your account details up to date,
              and protect your password and account access. Use your own authorised account and
              notify us promptly if you suspect unauthorised access.
            </p>
            <ul>
              <li>Use the service lawfully and only for information you are entitled to access or submit.</li>
              <li>Do not access another organisation’s data, bypass access controls, or impersonate another user.</li>
              <li>Do not upload malicious content, disrupt the service, or overload it through abusive automated requests.</li>
              <li>Do not misrepresent results, remove relevant qualifications, or claim that an assessment is a certification or guarantee.</li>
            </ul>
          </div>

          <div>
            <h3>3. Your information and use of outputs</h3>
            <p>
              You retain your rights in the information you submit. You grant 180Pi permission to
              host, process, and use that information to provide the service to your organisation.
              You are responsible for the accuracy of asset names, types, coordinates, addresses,
              business importance ratings, and value chain information, and for obtaining any
              necessary permissions to supply it.
            </p>
            <p>
              You may use the assessment outputs made available to your organisation for business
              planning and stakeholder communications, subject to your service agreement and any
              identified third-party restrictions. Include the relevant source attribution,
              assessment context, scenario, time horizon, and limitations when sharing results.
              Check that outputs are suitable for the intended audience and purpose.
            </p>
            <p>
              The platform’s software, design, branding, and proprietary methodology remain the
              property of 180Pi or its licensors. Third-party datasets and materials remain subject
              to their own licences. Access to the platform does not grant ownership of them or a
              right to resell the service or redistribute its underlying datasets.
            </p>
          </div>

          <div>
            <h3>4. Understanding climate risk results</h3>
            <p>
              The assessment framework covers heat stress, changing precipitation, water stress,
              drought, floods, and landslides. The hazards, locations, scenarios, time horizons,
              and outputs available to you depend on the data and features available in the
              service. A listed hazard or planned feature does not mean that every assessment
              or future projection is available.
            </p>
            <ul>
              <li>
                Scores are indicative categories based on selected datasets, thresholds, models,
                and your inputs. They are not probabilities of an event or estimates of financial loss.
              </li>
              <li>
                Historical baselines describe a reference period. Future scenarios explore possible
                conditions under stated assumptions; they do not predict a particular event or date.
              </li>
              <li>
                Gridded and basin-level data may not capture conditions at an individual asset,
                local terrain, drainage, existing protections, or recent changes. Location matching
                and inaccurate inputs can affect results.
              </li>
              <li>
                A low score, missing result, or “not applicable” label does not establish that a
                site is safe or free of climate risk. The framework does not assess every possible hazard.
              </li>
              <li>
                Datasets, methods, and coverage can change. Provisional results and their stated
                limitations should be considered when interpreting or comparing assessments.
              </li>
            </ul>
            <p>
              The service supports screening and planning. It does not replace site inspections,
              engineering studies, emergency alerts, or qualified environmental, legal, financial,
              insurance, or other professional advice. Outputs alone do not establish compliance
              with any disclosure standard or regulatory obligation. Obtain appropriate independent
              advice before making consequential investment, safety, or operational decisions.
            </p>
          </div>

          <div>
            <h3>5. Availability, suspension, and account closure</h3>
            <p>
              Access may be interrupted by maintenance, technical issues, or third-party service
              failures. Features and datasets may be updated or withdrawn. Any specific service
              levels or availability commitments must be set out in your service agreement.
            </p>
            <p>
              We may restrict or suspend access where reasonably necessary to address misuse,
              a security concern, or a legal requirement. Contact us to discuss account closure.
              Disabling access does not itself delete your organisation’s information; retention
              and deletion are addressed in the Privacy Policy.
            </p>
          </div>

          <div>
            <h3>6. Warranties and responsibility</h3>
            <p>
              To the extent permitted by applicable law and subject to any express commitments in
              your service agreement, the service and its outputs are provided on an “as available”
              basis. We do not guarantee that information will be complete, current, error-free,
              or suitable for every purpose, or that an assessment will prevent loss or damage.
              You remain responsible for your decisions and for validating information used in them.
            </p>
            <p>
              Nothing in these terms excludes or limits liability or rights that cannot lawfully
              be excluded or limited. If you have a concern about the service or these terms,
              contact us using the details below so we can try to resolve it.
            </p>
          </div>

          <div>
            <h3>7. Changes to these terms</h3>
            <p>
              Revised terms will identify their effective date. Material changes will be
              communicated through the service or the account email, with notice and renewed
              agreement where required by applicable law or your service agreement.
            </p>
          </div>
        </section>

        <section id="privacy-policy" aria-labelledby="privacy-heading" className="scroll-mt-6 space-y-6 border-t border-border pt-10">
          <h2 id="privacy-heading">Privacy Policy</h2>

          <div>
            <h3>1. Scope and responsibility</h3>
            <p>
              This policy describes information handled through 180Pi Climate Risk. Business and
              asset information can be personal data when it identifies or relates to an individual,
              including a sole trader or the owner of a site. Other 180Pi services may have separate
              privacy notices.
            </p>
            <p>
              OneEightyPi, Ahtri 12, Tallinn 15551, Estonia, is responsible for the personal data
              it processes to administer accounts and operate this service. You can contact us at
              support@180pi.com. Where we process personal data solely on your organisation’s
              instructions, the respective responsibilities must be set out in the applicable
              data-processing arrangement.
            </p>
          </div>

          <div>
            <h3>2. Information handled through the service</h3>
            <ul>
              <li>
                <strong>Account and organisation information:</strong> first and last name, email
                address, a password stored as a salted hash, company or workspace name, sector,
                account role, status, and creation or update dates.
              </li>
              <li>
                <strong>Asset and assessment information:</strong> asset names and types, addresses,
                coordinates, city, country, value chain stages, and business and value chain
                importance ratings. This information is used with climate data for assessments.
              </li>
              <li>
                <strong>Authentication and technical information:</strong> session identifiers,
                password-reset records, and operational logs such as request paths, response
                status, timing, and errors. Services contacted by your browser also receive
                connection information, such as your IP address and browser details.
              </li>
              <li>
                <strong>Communications:</strong> information in account emails and information
                you choose to provide when contacting us for assistance.
              </li>
            </ul>
            <p>
              Submit only information necessary for the assessment. Avoid including unrelated
              personal information, sensitive personal records, or secrets in asset names,
              addresses, and other free-text fields.
            </p>
          </div>

          <div>
            <h3>3. Purposes and legal grounds</h3>
            <p>
              Information is used to create and administer accounts, authenticate users, save and
              manage assets, match locations to climate data, support assessments, send welcome
              and password-reset emails, respond to requests, and maintain and troubleshoot the service.
              Required account and assessment information is necessary to provide the corresponding features.
            </p>
            <p>
              Customer account and assessment information is private. We do not sell it, use it
              for marketing, or use it to train AI models.
            </p>
            <p>
              Where required by applicable data protection law, our legal grounds are performance
              of a contract when providing a service directly to you; legitimate interests in
              administering our business clients’ accounts, responding to requests, and protecting
              the service, balanced against your rights; and compliance with applicable legal
              obligations. Where we act on your organisation’s instructions, that organisation
              is responsible for establishing the lawful basis for its use of personal data.
            </p>
            <p>
              Accepting the Terms of Service does not by itself provide consent for unrelated
              processing that requires a separate choice under applicable law.
            </p>
          </div>

          <div>
            <h3>4. Access and external services</h3>
            <p>
              Information is available to authorised users within your organisation’s workspace
              according to their access permissions. Platform administrators can access account,
              company, and asset information for administration. External providers receive the
              information needed for their service functions as described below. Private hosting
              of the main database does not prevent these limited service-related transfers.
            </p>
            <ul>
              <li>
                <strong>OVHcloud:</strong> provides the infrastructure for our private server and
                database, which store account and asset information. The server’s country is
                awaiting confirmation. See{" "}
                <a className="text-primary underline underline-offset-4" href="https://www.ovhcloud.com/en/terms-and-conditions/privacy-policy/">
                  OVHcloud’s Privacy Policy
                </a>.
              </li>
              <li>
                <strong>GoDaddy and Resend:</strong> provide services supporting our email setup
                and delivery of account notifications. Delivering email involves processing recipient
                addresses, message content, and delivery information. Welcome and password-reset
                messages are service communications. See the{" "}
                <a className="text-primary underline underline-offset-4" href="https://www.godaddy.com/en-ca/legal/agreements/privacy-policy">
                  GoDaddy Privacy Notice
                </a>{" "}
                and{" "}
                <a className="text-primary underline underline-offset-4" href="https://resend.com/legal/privacy-policy">
                  Resend Privacy Policy
                </a>.
              </li>
              <li>
                <strong>Cloudflare Turnstile:</strong> account forms use bot protection that
                processes signals including IP address and browser information. Cloudflare also
                uses signals to improve its bot detection. See the{" "}
                <a className="text-primary underline underline-offset-4" href="https://www.cloudflare.com/turnstile-privacy-policy/">
                  Cloudflare Turnstile Privacy Addendum
                </a>.
              </li>
              <li>
                <strong>OpenStreetMap:</strong> map tiles and Nominatim location search contact
                OpenStreetMap services directly from your browser. Address searches send the
                search text; selecting a map location sends its coordinates for address lookup.
                Map requests also reveal the area being viewed and connection information.
                Avoid putting confidential or personal details into map searches. See the{" "}
                <a className="text-primary underline underline-offset-4" href="https://osmfoundation.org/wiki/Privacy_Policy">
                  OpenStreetMap Foundation Privacy Policy
                </a>.
              </li>
              <li>
                <strong>Fonts and map images:</strong> the app loads fonts from Google Fonts and
                map marker images from Cloudflare’s cdnjs service. These browser requests disclose
                connection information to the respective providers. See{" "}
                <a className="text-primary underline underline-offset-4" href="https://policies.google.com/privacy">
                  Google’s Privacy Policy
                </a>{" "}
                and{" "}
                <a className="text-primary underline underline-offset-4" href="https://www.cloudflare.com/privacypolicy/">
                  Cloudflare’s Privacy Policy
                </a>.
              </li>
            </ul>
            <p>
              We do not share customer account or assessment information for unrelated commercial
              purposes. We may disclose information where required by applicable law or a binding
              legal process, limited to what that requirement calls for. Provider privacy notices
              explain their own processing; they do not replace our responsibilities for the
              information handled through this service.
            </p>
          </div>

          <div>
            <h3>5. Cookies and browser storage</h3>
            <p>
              The service uses an authentication cookie lasting up to 15 minutes and a refresh
              cookie lasting up to 12 hours to maintain your sign-in session. The authentication
              cookie can be renewed during that session. Signing out clears both cookies from
              your browser. Blocking these cookies prevents normal use of authenticated features.
            </p>
            <p>
              Browser local storage remembers whether the navigation sidebar is collapsed. This
              preference remains until it is changed or browser storage is cleared. You can manage
              cookies and site storage through your browser settings; clearing them may sign you out
              or reset preferences. External services described above also process browser signals
              under their own notices.
            </p>
          </div>

          <div>
            <h3>6. Storage locations, retention, and security</h3>
            <p>
              Accounts and saved asset information are held in a database on our private OVHcloud
              server. Disabling
              an account or company preserves its saved information. Asset deletion and company
              deletion are separate actions; deletion from the active service does not establish
              that every backup or operational log has been erased.
            </p>
            <p>
              The service uses password hashing, authenticated access, and workspace-based access
              checks. No online service can guarantee absolute security. Keep your credentials
              private and report suspected compromise promptly.
            </p>
            <p>
              Contact support@180pi.com to request account closure or deletion and to ask about
              the information retained for your account. External providers may process information
              in countries other than your own; the main server’s location alone does not determine
              where all processing takes place.
            </p>
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              [Pending confirmation before publication: the server country and other processing
              locations; applicable international-transfer safeguards and how to obtain details;
              retention periods or criteria for active and closed accounts, assets, communications,
              logs, and backups; and the deletion procedure and any legal retention exceptions.]
            </p>
          </div>

          <div>
            <h3>7. Your rights and requests</h3>
            <p>
              Depending on applicable law, you may have rights to receive information about the
              use of your personal data, access it, correct it, request deletion, object to or
              restrict certain uses, and receive a portable copy. Where processing relies on
              consent, you may withdraw it without affecting the lawfulness of earlier processing.
              These rights may be subject to legal conditions and exceptions.
            </p>
            <p>
              Contact the operator using the details below to make a request. We may need to verify
              your identity and, where your organisation controls the information, coordinate with
              its authorised representative. You may also complain to the relevant data protection
              authority. Requests will be handled within the time limits required by applicable law.
            </p>
          </div>

          <div>
            <h3>8. Updates to this policy</h3>
            <p>
              We will update this policy when the service or its information-handling practices
              change, identify the revised effective date, and provide notice of material changes.
              Where a new use requires consent, we will request it separately.
            </p>
          </div>
        </section>

        <section id="contact" aria-labelledby="contact-heading" className="scroll-mt-6 border-t border-border pt-10">
          <h2 id="contact-heading">Contact</h2>
          <p>For service, legal, privacy, or account-closure requests:</p>
          <ul>
            <li>Legal operator: OneEightyPi</li>
            <li>Registered address: Ahtri 12, Tallinn 15551, Estonia</li>
            <li>
              Email:{" "}
              <a className="text-primary underline underline-offset-4" href="mailto:support@180pi.com">support@180pi.com</a>
            </li>
            <li>
              Phone:{" "}
              <a className="text-primary underline underline-offset-4" href="tel:+256757122480">+256 757 122 480</a>
            </li>
          </ul>
        </section>
      </div>

      <footer className="mt-10 border-t border-border pt-6 text-sm">
        <a href="#top" className="text-primary underline underline-offset-4">Back to top</a>
      </footer>
    </main>
  );
}

export default Terms;
